import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

interface OrderRow {
  id: string;
  user_id: string;
  status: string;
  created_at: string;
}

interface GigRow {
  id: string;
  title: string;
  freelancer: string;
}

const statusLabels: Record<string, { text: string; cls: string }> = {
  pending: { text: 'En attente', cls: 'bg-secondary-500/10 text-secondary-400' },
  in_progress: { text: 'En cours', cls: 'bg-accent-500/10 text-accent-400' },
  completed: { text: 'Terminée', cls: 'bg-primary-500/10 text-primary-400' },
  cancelled: { text: 'Annulée', cls: 'bg-red-500/10 text-red-400' },
};

function GigOrders() {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [gig, setGig] = useState<GigRow | null>(null);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [buyers, setBuyers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    if (!user || !id) return;
    setLoading(true);
    setError('');
    try {
      const { data: gigData, error: gigErr } = await supabase
        .from('gigs')
        .select('id, title, freelancer')
        .eq('id', id)
        .maybeSingle();
      if (gigErr) throw gigErr;
      setGig(gigData as GigRow | null);

      const { data: ordersData, error: orderErr } = await supabase
        .from('gig_orders')
        .select('*')
        .eq('gig_id', id)
        .order('created_at', { ascending: false });
      if (orderErr) throw orderErr;
      const rows = (ordersData as OrderRow[]) || [];
      setOrders(rows);

      if (rows.length > 0) {
        const userIds = Array.from(new Set(rows.map((r) => r.user_id)));
        const { data: profiles, error: profileErr } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', userIds);
        if (!profileErr && profiles) {
          const map: Record<string, string> = {};
          (profiles as { id: string; full_name: string | null; email: string | null }[]).forEach(
            (p) => {
              map[p.id] = p.full_name || p.email || p.id;
            },
          );
          setBuyers(map);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les commandes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  const updateStatus = async (orderId: string, status: string) => {
    const { error: err } = await supabase
      .from('gig_orders')
      .update({ status })
      .eq('id', orderId);
    if (!err) {
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <Link
            to="/dashboard/gigs"
            className="inline-flex items-center gap-1 text-sm text-foreground-400 hover:text-accent-400 transition-colors mb-2"
          >
            <i className="ri-arrow-left-line" />
            Mes services
          </Link>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">
            Commandes reçues
          </h1>
          {gig && (
            <p className="text-sm text-foreground-400 mt-1">
              {gig.title} · {gig.freelancer}
            </p>
          )}
        </div>
        <span className="px-3 py-1.5 rounded-full bg-accent-500/10 text-accent-400 text-sm font-semibold whitespace-nowrap">
          {orders.length} commande{orders.length === 1 ? '' : 's'}
        </span>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-4">
          {error}
          <button onClick={load} className="ml-3 underline text-foreground-50">Réessayer</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/10">
            <i className="ri-loader-4-line text-2xl text-accent-400 animate-spin" />
          </div>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-accent-500/10">
            <i className="ri-shopping-cart-line text-3xl text-accent-400" />
          </div>
          <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">Aucune commande</h2>
          <p className="text-sm text-foreground-400">
            Aucun client n&apos;a encore commandé ce service.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => {
            const st = statusLabels[order.status] ?? statusLabels.pending;
            return (
              <div
                key={order.id}
                className="p-5 rounded-xl bg-background-100 border border-background-200/70"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-heading font-semibold text-foreground-50">
                        {buyers[order.user_id] || 'Client'}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${st.cls} whitespace-nowrap`}>
                        {st.text}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-500 mt-2">
                      Commandée le {new Date(order.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      value={order.status}
                      onChange={(e) => updateStatus(order.id, e.target.value)}
                      className="bg-background-50 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-50 outline-none focus:border-accent-400"
                    >
                      <option value="pending">En attente</option>
                      <option value="in_progress">En cours</option>
                      <option value="completed">Terminée</option>
                      <option value="cancelled">Annulée</option>
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}

export default GigOrders;